<template>
  <DefaultLayout>
    <div class="mx-auto mt-6 w-full max-w-[1400px] px-4">
      <PageHeader :to="{ name: 'dashboard' }" title="Meu perfil" />
      <div class="border border-stroke bg-white shadow-sm">
        <div class="border-b border-stroke p-6">
          <div class="flex items-center gap-4">
            <ProfileImage
              ref="profileImageRef"
              :avatar-url="userData.avatar_url"
              :user-name="userData.name"
              :editable="isEditing"
              @update:avatar="handleAvatarUpload" />
            <div>
              <h2 class="text-xl font-semibold text-gray-800">{{ userData.name }}</h2>
              <p class="text-base text-gray-500">{{ translatedRole }}</p>
            </div>
          </div>
      </div>

      <ProfileTabs v-model="activeTab" />

      <div class="p-6">
        <!-- Tab: Meu perfil -->
        <div v-show="activeTab === 'profile'" class="space-y-6">
          <div class="flex items-start justify-between gap-3">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">Informações e definições básicas</h3>
              <p class="mt-1 text-base text-slate-500">Você pode editar somente seus dados de usuário.</p>
            </div>
            <button
              v-if="!isEditing"
              class="border border-stroke px-4 py-2 text-base font-medium text-slate-700 hover:bg-slate-50"
              @click="startEditing">
              Editar perfil
            </button>
          </div>

          <form class="space-y-4" @submit.prevent="saveProfile">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label class="block">
                <span class="mb-1 block text-base font-medium text-slate-700">Nome</span>
                <input
                  v-model="form.name"
                  :disabled="!isEditing"
                  type="text"
                  class="w-full border border-stroke px-3 py-2 text-base text-slate-800 disabled:bg-slate-100" />
              </label>
              <label class="block">
                <span class="mb-1 block text-base font-medium text-slate-700">Email</span>
                <input
                  :value="form.email"
                  disabled
                  type="email"
                  class="w-full border border-stroke bg-slate-100 px-3 py-2 text-base text-slate-600" />
              </label>
              <label class="block">
                <span class="mb-1 block text-base font-medium text-slate-700">Telefone</span>
                <input
                  v-model="form.phone_number"
                  :disabled="!isEditing"
                  type="text"
                  placeholder="+5511999999999"
                  class="w-full border border-stroke px-3 py-2 text-base text-slate-800 disabled:bg-slate-100" />
              </label>
              <label class="block">
                <span class="mb-1 block text-base font-medium text-slate-700">Turno</span>
                <select
                  v-model="form.shift_id"
                  :disabled="!isEditing"
                  class="w-full border border-stroke px-3 py-2 text-base text-slate-800 disabled:bg-slate-100 disabled:text-slate-600">
                  <option value="">Sem turno específico</option>
                  <option v-for="shift in companyShifts" :key="shift.id" :value="shift.id">
                    {{ shift.name }} ({{ convertUtcToLocalTime(shift.start_time) }} - {{ convertUtcToLocalTime(shift.end_time) }})
                  </option>
                </select>
              </label>
              <label class="block">
                <span class="mb-1 block text-base font-medium text-slate-700">Perfil</span>
                <input
                  :value="translatedRole"
                  disabled
                  type="text"
                  class="w-full border border-stroke bg-slate-100 px-3 py-2 text-base text-slate-600" />
              </label>
            </div>
            <div class="mt-4 border-t border-stroke pt-4">
              <h3 class="mb-3 text-base font-semibold text-slate-700">Notificações</h3>
              <div class="flex flex-col gap-3">
                <label class="flex w-fit items-center gap-2 cursor-pointer">
                  <input v-model="form.receive_alerts" :disabled="!isEditing" type="checkbox" class="h-4 w-4 border-stroke text-primary focus:ring-primary" />
                  <span class="text-base text-slate-700">Receber alertas do sistema (Geral)</span>
                </label>

                <div class="ml-6 flex flex-wrap items-center gap-6" :class="{ 'opacity-50 pointer-events-none': !form.receive_alerts }">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input v-model="form.receive_push" :disabled="!isEditing || !form.receive_alerts" type="checkbox" class="h-4 w-4 border-stroke text-primary focus:ring-primary" />
                    <span class="text-base text-slate-700">Push</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input v-model="form.receive_whatsapp" :disabled="!isEditing || !form.receive_alerts" type="checkbox" class="h-4 w-4 border-stroke text-primary focus:ring-primary" />
                    <span class="text-base text-slate-700">WhatsApp</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input v-model="form.receive_email" :disabled="!isEditing || !form.receive_alerts" type="checkbox" class="h-4 w-4 border-stroke text-primary focus:ring-primary" />
                    <span class="text-base text-slate-700">Email</span>
                  </label>
                </div>
              </div>
            </div>

            <div v-if="isEditing" class="flex items-center gap-2 mt-6">
              <button
                type="button"
                class="border border-stroke px-4 py-2 text-base font-medium text-slate-700 hover:bg-slate-50"
                @click="cancelEditing">
                Cancelar
              </button>
              <button
                type="submit"
                :disabled="isSaving"
                class="border border-blue bg-blue px-4 py-2 text-base font-medium text-white disabled:opacity-60">
                {{ isSaving ? 'Salvando...' : 'Salvar alterações' }}
              </button>
            </div>
          </form>

          <div class="border-t border-stroke pt-6">
            <p class="text-base text-slate-600"><span class="font-semibold">Último acesso:</span> {{ lastAccess }}</p>
          </div>
        </div>

        <!-- Tab: Empresa -->
        <div v-show="activeTab === 'company'" class="space-y-4">
          <CollapsibleSection title="Informações da empresa" :default-open="true">
            <p class="mb-4 text-base text-slate-500">Dados da empresa em modo leitura, agrupados por contexto operacional.</p>
            <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div class="border border-stroke p-4">
                <h5 class="text-base font-semibold text-slate-900">Identificação</h5>
                <div class="mt-3 space-y-2 text-base text-slate-700">
                  <p><span class="font-semibold">Nome:</span> {{ companyData.name || '-' }}</p>
                  <p><span class="font-semibold">Registro/CNPJ:</span> {{ companyData.registration_number || '-' }}</p>
                  <p><span class="font-semibold">Tipo de negócio:</span> {{ translatedBusinessType }}</p>
                  <p><span class="font-semibold">Fuso horário:</span> {{ companyData.time_zone || '-' }}</p>
                </div>
              </div>
              <div class="border border-stroke p-4">
                <h5 class="text-base font-semibold text-slate-900">Operação e relatórios</h5>
                <div class="mt-3 space-y-2 text-base text-slate-700">
                  <p><span class="font-semibold">Worker de alarmes:</span> {{ boolLabel(companyData.alarms_worker_active) }}</p>
                  <p><span class="font-semibold">Relatório semanal:</span> {{ boolLabel(companyData.enabled_weekly_report) }}</p>
                </div>
              </div>
              <div class="border border-stroke p-4 lg:col-span-2">
                <h5 class="text-base font-semibold text-slate-900">Horários de referência</h5>
                <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div class="border border-stroke/70 bg-slate-50 p-3">
                    <p class="text-sm font-semibold uppercase tracking-wide text-slate-500">Horário de ponta</p>
                    <p class="mt-2 text-base text-slate-700"><span class="font-semibold">Início:</span> {{ convertUtcToLocalTime(companyData.peak_hour_start) }}</p>
                    <p class="text-base text-slate-700"><span class="font-semibold">Fim:</span> {{ convertUtcToLocalTime(companyData.peak_hour_end) }}</p>
                  </div>
                  <div class="border border-stroke/70 bg-slate-50 p-3">
                    <p class="text-sm font-semibold uppercase tracking-wide text-slate-500">Horário de madrugada</p>
                    <p class="mt-2 text-base text-slate-700"><span class="font-semibold">Início:</span> {{ convertUtcToLocalTime(companyData.dawn_hour_start) }}</p>
                    <p class="text-base text-slate-700"><span class="font-semibold">Fim:</span> {{ convertUtcToLocalTime(companyData.dawn_hour_end) }}</p>
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Turmas / Turnos" :default-open="false">
            <p class="mb-3 text-sm text-slate-500">Informações de turnos vinculados à empresa.</p>
            <div v-if="!companyShifts.length" class="text-base text-slate-500">Nenhum turno cadastrado.</div>
            <div v-else class="overflow-x-auto">
              <table class="min-w-full border-collapse text-base">
                <thead>
                  <tr class="border-b border-stroke text-left text-slate-600">
                    <th class="px-2 py-2 font-semibold">Nome</th>
                    <th class="px-2 py-2 font-semibold">Início</th>
                    <th class="px-2 py-2 font-semibold">Fim</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="shift in companyShifts" :key="shift.id" class="border-b border-stroke/70 text-slate-700">
                    <td class="px-2 py-2">{{ shift.name }}</td>
                    <td class="px-2 py-2">{{ convertUtcToLocalTime(shift.start_time) }}</td>
                    <td class="px-2 py-2">{{ convertUtcToLocalTime(shift.end_time) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Grupos de notificação" :default-open="false">
            <NotificationGroupsSection
              :groups="companyData.notification_groups"
              :is-admin="isAdmin" />
          </CollapsibleSection>

          <CollapsibleSection v-if="isAdmin && !!userData.notification_metrics" title="Notificações" :default-open="false">
            <NotificationMetricsCard :metrics="userData.notification_metrics" />
          </CollapsibleSection>
          <CollapsibleSection v-else-if="isAdmin" title="Notificações" :default-open="false">
            <div class="border border-dashed border-stroke p-8 text-center text-base text-slate-500">
              Métricas de notificação não disponíveis.
            </div>
          </CollapsibleSection>

          <CollapsibleSection v-if="isAdmin" title="Usuários" :default-open="false">
            <p class="mb-4 text-base text-slate-500">
              Visualize os colaboradores e seus dados de uso. Clique em uma linha para ver detalhes.
            </p>
            <CompanyUsersTable
              :users="userData.company_users"
              :company-id="companyData.id" />
          </CollapsibleSection>
        </div>
      </div>

      <div class="border-t border-stroke p-6">
        <h4 class="text-lg font-semibold text-slate-900">Sair do sistema</h4>
        <p class="mt-1 text-base text-slate-500">Finalize sua sessão de forma segura.</p>
        <button class="mt-4 border border-red-300 px-4 py-2 text-base font-medium text-red-600 hover:bg-red-50" @click="isConfirmLogoutOpen = true">
          Encerrar sessão
        </button>
      </div>
      </div>
    </div>
    <ConfirmationModal
      :is-open="isConfirmLogoutOpen"
      cancel-text="Cancelar"
      confirmation-text="Sair"
      @cancel="isConfirmLogoutOpen = false"
      @add="logoutHandler">
      <template #content>
        <div class="space-y-2">
          <h3 class="text-lg font-semibold text-slate-900">Deseja realmente sair?</h3>
          <p class="text-sm text-slate-600">Sua sessão será finalizada neste dispositivo.</p>
        </div>
      </template>
    </ConfirmationModal>
  </DefaultLayout>
</template>

<script setup lang="ts">
import ConfirmationModal from '@/components/Globals/ConfirmationModal.vue'
import PageHeader from '@/components/Globals/PageHeader.vue'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import ProfileImage from '@/components/UserProfile/ProfileImage.vue'
import ProfileTabs from '@/components/UserProfile/ProfileTabs.vue'
import NotificationMetricsCard from '@/components/UserProfile/NotificationMetricsCard.vue'
import CompanyUsersTable from '@/components/UserProfile/CompanyUsersTable.vue'
import CollapsibleSection from '@/components/UserProfile/CollapsibleSection.vue'
import NotificationGroupsSection from '@/components/UserProfile/NotificationGroupsSection.vue'
import UserResource from '@/api/resources/user'
import { ICompany } from '@/interfaces/entities/company'
import { IUser } from '@/interfaces/entities/user'
import { translateBusinessType, translateUserRole, UserRoles } from '@/interfaces/enums'
import { disconnectActionCable } from '@/services/actioncable.service'
import { clearAllStores } from '@/services/clear-stores'
import { useCompanyStore } from '@/store/company'
import { useSessionStore } from '@/store/session'
import { useUserStore } from '@/store/user'
import { logFirebaseAnalytics } from '@/utils/firebase'
import { useSuccessStore } from '@/store/success'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { formatOutsideRegularUtcTimeToLocalHHmm } from '@/utils/date/date-time'

const userStore = useUserStore()
const companyStore = useCompanyStore()
const userData = computed<IUser>(() => userStore.getCurrentUser as IUser)
const companyData = computed(
  (): ICompany => ((userData.value.default_company as ICompany) || (companyStore.getCurrentCompany as ICompany)),
)
const companyShifts = computed(() => {
  const shiftsFromCompany = (companyData.value as any)?.shifts
  if (Array.isArray(shiftsFromCompany) && shiftsFromCompany.length) {
    return shiftsFromCompany
  }
  return companyStore.shifts || []
})
const activeTab = ref<'profile' | 'company'>('profile')
const profileImageRef = ref<InstanceType<typeof ProfileImage> | null>(null)
const isConfirmLogoutOpen = ref(false)
const isEditing = ref(false)
const isSaving = ref(false)

const form = reactive({
  name: '',
  email: '',
  phone_number: '',
  receive_alerts: false,
  receive_push: false,
  receive_whatsapp: false,
  receive_email: false,
  shift_id: '' as number | string,
})

const translatedRole = computed(() => translateUserRole(userData.value.role))
const isAdmin = computed(() => userData.value.role === UserRoles.ADMIN)
const translatedBusinessType = computed(() =>
  translateBusinessType(companyData.value.type_of_business),
)
const lastAccess = computed(() => {
  if (!userData.value.last_sign_in_at) return 'Não informado'
  return new Date(userData.value.last_sign_in_at).toLocaleString('pt-BR')
})

const convertUtcToLocalTime = (timeValue?: string | null) => {
  if (!timeValue) return '-'
  return formatOutsideRegularUtcTimeToLocalHHmm(timeValue) || timeValue
}

const syncFormWithUser = () => {
  form.name = userData.value.name || ''
  form.email = userData.value.email || ''
  form.phone_number = userData.value.phone_number || ''
  form.receive_alerts = !!userData.value.receive_alerts
  form.receive_push = !!userData.value.receive_push
  form.receive_whatsapp = !!userData.value.receive_whatsapp
  form.receive_email = !!userData.value.receive_email
  form.shift_id = userData.value.shift_id || (userData.value.shift && userData.value.shift.id) || ''
}

watch(
  () => userData.value,
  () => syncFormWithUser(),
  { immediate: true },
)


onMounted(async () => {
  try {
    await userStore.fetchCurrentUser()
    if (companyStore.getCurrentCompanyId) {
      await companyStore.fetchCompanyShifts()
    }
  } catch (error) {
    // Mantem os dados do login em caso de falha temporária.
  }
})

const boolLabel = (value?: boolean) => (value ? 'Ativo' : 'Inativo')

const startEditing = () => {
  isEditing.value = true
}

const cancelEditing = () => {
  isEditing.value = false
  syncFormWithUser()
}

const handleAvatarUpload = async (file: File) => {
  try {
    await userStore.uploadAvatar(file)
    useSuccessStore().setSuccess(['Foto atualizada com sucesso.'])
  } catch (error: any) {
    console.error(error)
  } finally {
    profileImageRef.value?.setUploadComplete()
  }
}

const saveProfile = async () => {
  if (!userData.value.id) return
  isSaving.value = true
  try {
    await userStore.updateProfile({
      id: userData.value.id,
      name: form.name.trim(),
      email: form.email.trim(),
      phone_number: form.phone_number?.trim() || undefined,
      receive_alerts: form.receive_alerts,
      receive_push: form.receive_push,
      receive_whatsapp: form.receive_whatsapp,
      receive_email: form.receive_email,
      shift_id: form.shift_id ? Number(form.shift_id) : null,
    })
    isEditing.value = false
    useSuccessStore().setSuccess(['Perfil atualizado com sucesso.'])
  } catch (error: any) {
    console.error(error)
  } finally {
    isSaving.value = false
  }
}

const logoutHandler = async () => {
  logFirebaseAnalytics('logout', { time: new Date().toISOString() })
  await UserResource.signOut().catch(() => {})
  disconnectActionCable()
  useSessionStore().destroySession()
  clearAllStores()
}
</script>
